import { useState, useMemo, useEffect } from 'react';

/**
 * Hook for pagination logic
 * @param {Array} items - Items to paginate
 * @param {number} itemsPerPage - Items per page
 * @param {unknown} resetKey - When this value changes, pagination resets to page 1
 * @returns {Object} Pagination state and helpers
 */
export const usePagination = (items, itemsPerPage = 7, resetKey) => {
    const [currentPage, setCurrentPage] = useState(1);

    // Reset to page 1 when resetKey changes
    useEffect(() => {
        setCurrentPage(1);
    }, [resetKey]);

    const totalPages = Math.ceil(items.length / itemsPerPage);
    const paginatedItems = useMemo(() => {
        return items.slice(
            (currentPage - 1) * itemsPerPage,
            currentPage * itemsPerPage
        );
    }, [items, currentPage, itemsPerPage]);

    const goToPage = (page) => {
        setCurrentPage(Math.max(1, Math.min(page, totalPages)));
    };

    const nextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    const prevPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    return {
        currentPage,
        totalPages,
        paginatedItems,
        goToPage,
        nextPage,
        prevPage,
        setCurrentPage,
    };
};

